import { Children, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { s, typographyTokens } from '@/theme/tokens';
import { Box, Link, Text, type Theme, Tooltip, type TooltipProps } from '@/ui';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '@/ui/composites/table/tableTooltip';
import type { SxProps } from '@/ui/primitives';

export const CLONE_META_DETAIL_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: s('sm'),
  pl: s('xxs'),
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
} as const;

export const CLONE_META_DETAIL_VALUE_SX = {
  display: 'block',
  minWidth: 0,
  flex: 1,
  py: '0.5px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const CLONE_META_DETAIL_GLYPH_SX = {
  fontSize: (theme: Theme) => theme.app.iconSizes.sm,
} as const;

const CLONE_META_DETAIL_ICON_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  transform: 'translateY(-2px)',
  flex: '0 0 auto',
} as const;

export const CLONE_META_DETAIL_TEXT_SX = {
  '&&': {
    fontSize: '13px',
    fontWeight: typographyTokens.captionSmall.fontWeight,
    lineHeight: 1.1,
  },
} as const;

export const CLONE_META_DETAILS_LINK_SX = {
  display: 'inline-block',
  verticalAlign: 'top',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '13px !important',
  fontWeight: `${typographyTokens.captionSmall.fontWeight} !important`,
  lineHeight: '1.1 !important',
} as const;

export const CLONE_META_VALUE_WRAPPER_SX = {
  display: 'inline-block',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const CLONE_TOOLTIP_SLOT_PROPS: TooltipProps['slotProps'] = {
  tooltip: {
    sx: {
      fontSize: '15px',
      lineHeight: 1.4,
    },
  },
} as const;

type TableMetaRowProps = {
  title: string;
  icon: ReactElement;
  value: ReactNode;
  valueTitle?: string | null;
  href?: string | null;
  disableLinks?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  textSx?: object;
  linkSx?: object;
  contentSx?: SxProps<Theme>;
  valueWrapperSx?: SxProps<Theme>;
};

const renderValueContent = ({
  value,
  href,
  disableLinks,
  onClick,
  linkSx,
  valueWrapperSx,
}: Pick<
  TableMetaRowProps,
  'value' | 'href' | 'disableLinks' | 'onClick' | 'linkSx' | 'valueWrapperSx'
>): ReactElement => {
  if (disableLinks || href === null || href === undefined) {
    return (
      <Box
        component="span"
        sx={[
          CLONE_META_VALUE_WRAPPER_SX,
          ...(Array.isArray(valueWrapperSx)
            ? valueWrapperSx
            : valueWrapperSx == null
              ? []
              : [valueWrapperSx]),
        ]}
      >
        {value}
      </Box>
    );
  }

  return (
    <Link
      component={RouterLink}
      to={href}
      underline="hover"
      color="inherit"
      onClick={onClick}
      sx={linkSx ?? CLONE_META_DETAILS_LINK_SX}
    >
      {value}
    </Link>
  );
};

type TableMetaInlineRowProps = {
  icon: ReactElement;
  content: ReactNode;
  textSx?: object;
  rowSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
};

export const TableMetaInlineRow = ({
  icon,
  content,
  textSx,
  rowSx,
  contentSx,
}: TableMetaInlineRowProps): ReactElement => {
  const resolvedRowSx: SxProps<Theme> = [
    CLONE_META_DETAIL_ROW_SX,
    ...(Array.isArray(rowSx) ? rowSx : rowSx == null ? [] : [rowSx]),
  ];
  const resolvedContentSx: SxProps<Theme> = [
    CLONE_META_DETAIL_VALUE_SX,
    ...(Array.isArray(contentSx) ? contentSx : contentSx == null ? [] : [contentSx]),
  ];
  return (
    <Text
      role="captionSmall"
      color="textSecondary"
      dataGridTypography="custom"
      sx={textSx ?? CLONE_META_DETAIL_TEXT_SX}
    >
      <Box component="span" sx={resolvedRowSx}>
        <Box component="span" sx={CLONE_META_DETAIL_ICON_SX}>
          {icon}
        </Box>
        <Box component="span" sx={resolvedContentSx}>
          {content}
        </Box>
      </Box>
    </Text>
  );
};

type TableMetaInlineValueRowProps = {
  title?: string;
  icon: ReactElement;
  content: ReactNode;
  valueTitle?: string | null;
  textSx?: object;
  rowSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  valueWrapperSx?: SxProps<Theme>;
};

export const TableMetaInlineValueRow = ({
  title,
  icon,
  content,
  valueTitle,
  textSx,
  rowSx,
  contentSx,
  valueWrapperSx,
}: TableMetaInlineValueRowProps): ReactElement => (
  <TableMetaInlineRow
    icon={
      title ? (
        <Tooltip
          title={title}
          arrow
          enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <Box component="span">{icon}</Box>
        </Tooltip>
      ) : (
        icon
      )
    }
    textSx={textSx}
    rowSx={rowSx}
    contentSx={contentSx}
    content={
      <Tooltip
        title={valueTitle ?? ''}
        arrow
        enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
        slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        disableHoverListener={!valueTitle}
      >
        <Box
          component="span"
          sx={[
            CLONE_META_VALUE_WRAPPER_SX,
            ...(Array.isArray(valueWrapperSx)
              ? valueWrapperSx
              : valueWrapperSx == null
                ? []
                : [valueWrapperSx]),
          ]}
        >
          {content}
        </Box>
      </Tooltip>
    }
  />
);

export const TableMetaRow = ({
  title,
  icon,
  value,
  valueTitle,
  href,
  disableLinks = false,
  onClick,
  textSx,
  linkSx,
  contentSx,
  valueWrapperSx,
}: TableMetaRowProps): ReactElement => {
  const content = renderValueContent({
    value,
    href,
    disableLinks,
    onClick,
    linkSx,
    valueWrapperSx,
  });

  return (
    <Text
      role="captionSmall"
      color="textSecondary"
      dataGridTypography="custom"
      sx={textSx ?? CLONE_META_DETAIL_TEXT_SX}
    >
      <Box component="span" sx={CLONE_META_DETAIL_ROW_SX}>
        <Tooltip
          title={title}
          arrow
          enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <Box component="span" sx={CLONE_META_DETAIL_ICON_SX}>
            {icon}
          </Box>
        </Tooltip>
        <Box
          component="span"
          sx={[
            CLONE_META_DETAIL_VALUE_SX,
            ...(Array.isArray(contentSx) ? contentSx : contentSx == null ? [] : [contentSx]),
          ]}
        >
          <Tooltip
            title={valueTitle ?? ''}
            arrow
            enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
            slotProps={CLONE_TOOLTIP_SLOT_PROPS}
            disableHoverListener={!valueTitle}
          >
            <Box component="span" sx={[CLONE_META_VALUE_WRAPPER_SX, { width: 'fit-content' }]}>
              {content}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Text>
  );
};

type TableMetaStackProps = {
  children: ReactNode;
  rowGap?: string;
  sx?: SxProps<Theme>;
};

export const TableMetaStack = ({
  children,
  rowGap = s('md'),
  sx,
}: TableMetaStackProps): ReactElement => {
  return (
    <Box
      sx={[
        {
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          rowGap,
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      {Children.toArray(children)}
    </Box>
  );
};

export const TABLE_BREAKDOWN_STACK_ROW_GAP = s('xs');

type TableBreakdownStackProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
};

export const TableBreakdownStack = ({ children, sx }: TableBreakdownStackProps): ReactElement => (
  <TableMetaStack rowGap={TABLE_BREAKDOWN_STACK_ROW_GAP} sx={sx}>
    {children}
  </TableMetaStack>
);
